import { useEffect, useState } from 'react';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { router } from './router.js';
import { ApiClientError, bootstrapSession, onAuthChange, api, logout } from '../lib/api/client.js';
import { useSession } from '../lib/auth/session.js';
import type { SessionBootstrap } from '@arcnet/contracts';
import { LoginPage } from '../features/auth/LoginPage.js';
import { AcceptInvitePage } from '../features/auth/AcceptInvitePage.js';
import { AuthLayout } from '../features/auth/AuthLayout.js';
import { Toaster, toastError } from '../components/ui/Toast.js';

const queryClient = new QueryClient({
  /**
   * A failed save or action is a toast, wherever it happened (components/ui/Toast.tsx,
   * owner, 15 September 2026). A form that says its own failure in place sets
   * `meta: { toast: false }` on its mutation. A 401 is the session ending, which the
   * sign-in screen says by appearing.
   */
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.['toast'] === false) return;
      if ((error as { status?: number }).status === 401) return;
      // Not a failure: the step-up prompt asks for the password and retries (lib/auth/stepup.tsx).
      if (error instanceof ApiClientError && error.code === 'auth.step_up_required') return;
      toastError(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, error) => {
        // Never retry an auth or authorisation failure: the client already
        // attempted a single-flight refresh, so retrying just burns requests
        // and delays showing the login screen.
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403 || status === 404) return false;
        return count < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(false);
  const setSession = useSession((s) => s.setSession);
  const clear = useSession((s) => s.clear);

  useEffect(() => onAuthChange((v) => { if (!v) { clear(); setAuthed(false); } }), [clear]);

  useEffect(() => {
    // An existing HttpOnly refresh cookie silently restores the session, so a
    // page reload does not send the user back to the login screen.
    void (async () => {
      const ok = await bootstrapSession();
      if (ok) {
        try {
          const me = await api.get<SessionBootstrap>('/v1/auth/me');
          setSession(me);
          setAuthed(true);
        } catch { setAuthed(false); }
      }
      setReady(true);
    })();
  }, [setSession]);

  const onSignedIn = async (): Promise<void> => {
    const me = await api.get<SessionBootstrap>('/v1/auth/me');
    setSession(me);
    setAuthed(true);
  };

  if (!ready) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <div className="arc-skeleton" style={{ width: 180, height: 10 }} />
      </div>
    );
  }

  // Invitation acceptance is checked BEFORE the auth gate: the person following
  // the link has no account yet, so there is nothing to authenticate. Read
  // straight from the URL rather than through the router, which only mounts
  // once a session exists.
  const inviteToken = /^\/invite\/([\w.-]+)$/.exec(window.location.pathname)?.[1];
  /**
   * An invitation opened in a browser that is already signed in (owner, 18 September 2026: the link
   * went straight into the admin console, and the invited person was never asked for a password).
   * It is for somebody new, so it asks to sign out first; staying signed in goes to the portal.
   */
  if (inviteToken && authed) {
    return (
      <AuthLayout>
            <h1 className="arc-h1">Accept an invitation</h1>
            <p className="arc-muted" style={{ marginTop: 6, fontSize: 13.5, lineHeight: 1.55 }}>
              You are signed in as <strong>{useSession.getState().session?.user.email}</strong>. An invitation
              makes a new sign-in for the person it was sent to, so sign out first to accept it.
            </p>
            <div className="arc-stack" style={{ gap: 8, marginTop: 18 }}>
              <button type="button" className="arc-btn arc-btn-primary" style={{ width: '100%' }}
                onClick={() => { void logout(); }}>
                Sign out and accept
              </button>
              <button type="button" className="arc-btn" style={{ width: '100%' }}
                onClick={() => { window.location.assign('/'); }}>
                Stay signed in
              </button>
            </div>
      </AuthLayout>
    );
  }
  if (inviteToken && !authed) {
    return (
      <QueryClientProvider client={queryClient}>
        <AcceptInvitePage token={inviteToken} onAccepted={onSignedIn} />
        <Toaster />
      </QueryClientProvider>
    );
  }

  if (!authed) {
    return (
      <QueryClientProvider client={queryClient}>
        <LoginPage onSignedIn={onSignedIn} />
        <Toaster />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster />
    </QueryClientProvider>
  );
}
